import { IconGavel, IconIdBadge2, IconSchool } from "@tabler/icons-react";
import { Navigate, RouteObject } from "react-router-dom";
import { routeCan } from "src/authentication/routeCan";
import { Action } from "src/casl/Action";

/**
 * The officer's screens gate on `Manage`, as the API does. Everybody holds a
 * `Read` scoped to their own row, and CASL answers a check with no row against
 * the rule, so a `Read` gate would let any volunteer in. The elders' branch
 * gates on `Create` of a decision because an applicant can read their own
 * application. The parent has no gate: `useRouteCans` requires every matched
 * handle to pass, so one there would shut the elders out.
 */
const clearancesRoutes: RouteObject = {
    path: "clearances",
    handle: { crumb: "Clearances", icon: IconIdBadge2 },

    children: [
        {
            index: true,
            lazy: () => import("./clearances/ClearanceBoardScreen"),
            handle: routeCan(Action.Manage, "ChildProtectionClearance"),
        },

        {
            path: "inbox",
            lazy: () => import("./clearances/ClearanceInboxScreen"),
            handle: { crumb: "Needing clearance", ...routeCan(Action.Manage, "ChildProtectionClearance") },
        },

        {
            path: "start",
            lazy: () => import("./clearances/StartClearanceScreen"),
            handle: { crumb: "Start a clearance", ...routeCan(Action.Create, "ChildProtectionClearance") },
        },

        {
            path: "training",
            handle: { crumb: "Training", icon: IconSchool, ...routeCan(Action.Manage, "ChildProtectionTraining") },

            children: [
                { index: true, lazy: () => import("./clearances/training/TrainingIndexScreen") },
                {
                    path: "session",
                    handle: { crumb: "Several people" },
                    lazy: () => import("./clearances/training/RecordSessionForSeveralScreen"),
                },
                { path: ":personId", lazy: () => import("./clearances/training/PersonTrainingScreen") },
            ],
        },

        {
            path: "elders-approval",
            handle: {
                crumb: "Elders' approval",
                icon: IconGavel,
                ...routeCan(Action.Create, "ChildProtectionEldersDecision"),
            },

            children: [
                { index: true, lazy: () => import("./clearances/elders/ApplicationsAwaitingDecisionScreen") },
                {
                    path: ":preliminaryApplicationId",
                    lazy: () => import("./clearances/elders/SignEldersDecisionScreen"),
                },
            ],
        },

        {
            path: ":clearanceId",
            handle: routeCan(Action.Manage, "ChildProtectionClearance"),

            children: [
                /** The tabs, under a layout without a path, so the paper entry screens sit beside them */
                {
                    lazy: () => import("./clearances/ViewClearanceScreen"),
                    children: [
                        { index: true, lazy: () => import("./clearances/detail/tabs/ClearanceOverviewTab") },
                        { path: "forms", lazy: () => import("./clearances/detail/tabs/ClearanceFormsTab") },
                        { path: "access-ni", lazy: () => import("./clearances/detail/tabs/ClearanceAccessNiTab") },
                        { path: "references", lazy: () => import("./clearances/detail/tabs/ClearanceReferencesTab") },
                        { path: "notes", lazy: () => import("./clearances/detail/tabs/ClearanceNotesTab") },
                    ],
                },

                {
                    path: "record",
                    handle: { crumb: "Record from paper" },

                    children: [
                        {
                            path: "preliminary-application",
                            lazy: () => import("./clearances/officer-entry/RecordPaperPreliminaryApplicationScreen"),
                        },
                        {
                            path: "preliminary-application/:preliminaryApplicationId",
                            lazy: () => import("./clearances/officer-entry/RecordPaperPreliminaryApplicationScreen"),
                        },

                        {
                            path: "application-form",
                            lazy: () => import("./clearances/officer-entry/RecordPaperApplicationFormScreen"),
                        },
                        {
                            path: "application-form/:applicationFormId",
                            lazy: () => import("./clearances/officer-entry/RecordPaperApplicationFormScreen"),
                        },

                        {
                            path: "elders-decision",
                            lazy: () => import("./clearances/officer-entry/RecordPaperEldersDecisionScreen"),
                        },
                        {
                            path: "elders-decision/:eldersDecisionId",
                            lazy: () => import("./clearances/officer-entry/RecordPaperEldersDecisionScreen"),
                        },

                        {
                            path: "reference/:referenceId",
                            lazy: () => import("./clearances/officer-entry/RecordPaperReferenceScreen"),
                        },
                    ],
                },
            ],
        },
    ],
};

export const childProtectionRoutes: RouteObject[] = [
    clearancesRoutes,

    {
        children: [
            {
                path: "forms",

                children: [
                    {
                        path: "parental-consent-forms",

                        children: [
                            {
                                index: true,
                                lazy: () => import("./forms/parental-consent-forms/ManageParentalConsentFormsScreen"),
                            },
                            {
                                path: "view-form/:parentalConsentFormId",
                                lazy: () => import("./forms/parental-consent-forms/ViewParentalConsentFormScreen"),
                            },

                            {
                                path: "submit-paper-form",
                                children: [
                                    { index: true, element: <Navigate to="about-your-child" replace /> },

                                    {
                                        lazy: () =>
                                            import("./forms/parental-consent-forms/paper/EnterPaperConsentFormScreen"),
                                        children: [
                                            {
                                                path: "about-your-child",
                                                lazy: () =>
                                                    import("../public/consent-and-medical/give-parental-consent/steps/PublicGiveParentalConsentFormAboutYourChildStep"),
                                            },
                                            {
                                                path: "your-details",
                                                lazy: () =>
                                                    import("../public/consent-and-medical/give-parental-consent/steps/PublicGiveParentalConsentFormYourDetailsStep"),
                                            },
                                            {
                                                path: "consent",
                                                lazy: () =>
                                                    import("../public/consent-and-medical/give-parental-consent/steps/PublicGiveParentalConsentFormConsentStep"),
                                            },
                                            {
                                                path: "declaration",
                                                lazy: () =>
                                                    import("./forms/parental-consent-forms/paper/PaperConsentFormDeclarationStep"),
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
];
